/**
 * The screen after the booking is made (owner, 2026-10-21, layout B of the mockup round).
 *
 * A connected channel starts working only after the client writes to it. The salon's own
 * number answers only clients who wrote first (inbox README §5.1), and a bot writes only
 * to clients who pressed Start. Until then no confirmation or reminder arrives, and the
 * client can't confirm or cancel in the chat. So when the client picked such a channel,
 * the screen calls that step required, says what it opens, and makes it the one action.
 * A client who picked the phone call is offered no messenger (owner). "Book again" and
 * the account are quiet links right under the panel, so every viewport shows them.
 * The salon's official WhatsApp (inbox plan INB4) needs no step: the salon writes first to
 * the number she booked with, so the note just says where the messages come.
 * Under the panel, after the step, the visit can go into her own calendar (boona
 * google-calendar README §9.7): quiet links in the order her device wants them.
 */
import { Bell, CalendarCheck, Check, Copy, MessageCircle, Phone } from "lucide-react";
import { useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { AddToCalendar } from "../../../components/AddToCalendar";
import { Mark, PaperPlane, useChannelName } from "./ConfirmChannels";
import type { PersonalLink, StatusPanel } from "./OverviewView";

/** the button colour, and a darker ink for text on a tint of it */
const TONE: Record<string, string> = { whatsapp: "#25D366", telegram: "#2AABEE", max: "#5B4BFF", vk: "#0077FF" };
const INK: Record<string, string> = { whatsapp: "#128C4B", telegram: "#1C84BF", max: "#4A3BE0", vk: "#0062D1" };

/** the chat the client still has to open: a bot to start, or the salon's own number to write to */
type Step = { kind: "bot" | "own"; channel: string; url: string | null; code?: string; text?: string; phone?: string | null };

/** the required step, if the client's choice needs one, and the salon's other own accounts
 *  (the same code works there) */
function plan(s: StatusPanel): { step?: Step; others: PersonalLink[] } {
  if (s.confirmMethod === 1 || s.messengerKind === "official") return { others: [] };
  const links = s.personalLinks ?? [];
  if (s.messengerLink) {
    const channel = /^https:\/\/max\.ru\//.test(s.messengerLink) ? "max" : /^https:\/\/vk\.me\//.test(s.messengerLink) ? "vk" : "telegram";
    return { step: { kind: "bot", channel, url: s.messengerLink }, others: links };
  }
  const picked = links.find((l) => l.channel === s.messengerChannel);
  // a returning client already chats with the salon there: no step (the note says where it comes)
  if (picked && !picked.bound) return { step: { kind: "own", ...picked }, others: links.filter((l) => l !== picked && !l.bound) };
  return { others: [] };
}

export default function BookedScreen({ status, onOpenClientDashboard }: {
  status: StatusPanel;
  onOpenClientDashboard?: () => void;
}) {
  const { t } = useTranslation();
  const { step, others } = plan(status);
  const d = status.details;
  const line = d ? [d.when, d.specialist].filter(Boolean).join(" · ") : status.summary;
  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col overflow-y-auto sm:rounded-2xl">
          <div className="px-5 pt-7 text-center" role="status" aria-live="polite">
            <div className="mx-auto mb-2.5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check className="h-6 w-6" strokeWidth={3} />
            </div>
            <div className="text-xl font-bold">{t("widget.overview.booked", "You're booked")}</div>
            {d?.service ? <div className="mt-1 text-sm">{d.service}</div> : null}
            {line ? <div className="text-sm text-muted-foreground">{line}</div> : null}
          </div>
          <div className="p-4 pb-0">
            {step ? <Unlocks step={step} others={others} /> : <Note status={status} />}
          </div>
          <AddToCalendar links={status.calendar} className="px-4 pt-5" />
          <QuietLinks status={status} onOpenClientDashboard={onOpenClientDashboard} />
        </Card>
      </div>
    </div>
  );
}

function Unlocks({ step, others }: { step: Step; others: PersonalLink[] }) {
  const { t } = useTranslation();
  const messenger = useChannelName()({ id: step.channel });
  const own = step.kind === "own";
  // wa.me and t.me links carry the code pre-typed; a MAX link opens the profile only
  const typed = own && !/[?&]text=/.test(step.url ?? "");
  return (
    <div className="rounded-2xl border-2 p-4" style={{ borderColor: TONE[step.channel] }}>
      <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
        {t("widget.booked.required", "Required step")}
      </span>
      <div className="mt-2 text-[17px] font-bold leading-snug">
        {own
          ? t("widget.booked.title_own", "Write to the salon in {{messenger}}", { messenger })
          : t("widget.booked.title_bot", "Start the salon's bot in {{messenger}}", { messenger })}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {own
          ? t("widget.booked.sub_own", "The salon can write to you in {{messenger}} only after your message. It opens:", { messenger })
          : t("widget.booked.sub_bot", "The bot can write to you only after you press Start. It opens:")}
      </p>
      <ul className="mt-3 space-y-2 text-sm">
        <Benefit channel={step.channel} icon={<CalendarCheck className="h-4 w-4" />}>
          {t("widget.booked.gets_receipt", "the booking confirmation, right away")}
        </Benefit>
        <Benefit channel={step.channel} icon={<Bell className="h-4 w-4" />}>
          {t("widget.booked.gets_reminder", "a reminder before the visit")}
        </Benefit>
        <Benefit channel={step.channel} icon={<Check className="h-4 w-4" strokeWidth={3} />}>
          {own
            ? t("widget.booked.gets_manage_own", "confirm or cancel the booking by replying “1” or “2”")
            : t("widget.booked.gets_manage_bot", "confirm or cancel the booking with a button in the chat")}
        </Benefit>
      </ul>
      <div className="mt-4">
        <StepButton step={step} typed={typed} />
      </div>
      {own && step.code ? (
        typed ? (
          <>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {t("widget.booked.code_send", "Send the code {{code}} in the chat.", { code: step.code })}
            </p>
            <CopyCode step={step} />
          </>
        ) : (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {t("widget.booked.code_ready", "The code {{code}} is already in the message: just tap “Send”.", { code: step.code })}{" "}
            <CopyCode step={step} short />
          </p>
        )
      ) : null}
      <OtherChats others={others} />
    </div>
  );
}

/** no required step: the phone call, a channel the admin answers by hand, or a bot the
 *  client already started */
function Note({ status }: { status: StatusPanel }) {
  const { t } = useTranslation();
  const name = useChannelName();
  const ch = status.confirmMethod === 2 ? status.messengerChannel : undefined;
  const messenger = ch ? name({ id: ch, label: status.messengerLabel }) : "";
  const text = !ch
    ? t("widget.confirm.hint_phone", "The salon will call you to confirm the visit.")
    : status.messengerKind === "official"
      ? t("widget.booked.note_official", "The confirmation and the reminder will come to your number in {{messenger}} from the salon — confirm with the button there.", { messenger })
      : status.messengerAuto
        ? t("widget.booked.note_auto", "The confirmation and the reminder will come to {{messenger}}.", { messenger })
        : t("widget.messenger.picker_hint_manual", "The salon will contact you in {{messenger}} to confirm.", { messenger });
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-3.5 text-sm">
      {ch ? <Mark id={ch} size="sm" /> : <Phone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
      <span>{text}</span>
    </div>
  );
}

function Benefit({ channel, icon, children }: { channel: string; icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2.5">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `${TONE[channel] ?? "#64748b"}1f`, color: INK[channel] ?? "#475569" }}
      >
        {icon}
      </span>
      {children}
    </li>
  );
}

function StepButton({ step, typed }: { step: Step; typed: boolean }) {
  const { t } = useTranslation();
  const messenger = useChannelName()({ id: step.channel });
  if (!step.url) {
    // no link form (MAX without a profile link): the number to write to
    return (
      <div className="rounded-xl border px-3 py-2.5 text-center text-sm">
        {t("widget.personal.write_to_number", "{{messenger}}: write to {{phone}}", { messenger, phone: step.phone ?? "" })}
      </div>
    );
  }
  const label = step.kind === "bot" || typed
    ? t("widget.messenger.open_app", "Open {{messenger}}", { messenger })
    : t("widget.booked.send_code", "Send the code in {{messenger}}", { messenger });
  const style: CSSProperties = { backgroundColor: TONE[step.channel] ?? "hsl(var(--primary))" };
  return (
    <a
      href={step.url}
      target="_blank"
      rel="noopener noreferrer"
      style={style}
      className="flex h-[54px] items-center justify-center gap-2.5 rounded-xl text-[17px] font-bold text-white shadow-md transition-opacity hover:opacity-90"
    >
      {step.channel === "telegram"
        ? <PaperPlane className="h-[22px] w-[22px]" />
        : <MessageCircle className="h-[22px] w-[22px]" aria-hidden="true" />}
      {label}
    </a>
  );
}

function CopyCode({ step, short = false }: { step: Step; short?: boolean }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (!step.code) return null;
  const copy = async () => {
    try {
      // the pre-typed text where there is one, else the bare code to paste
      await navigator.clipboard.writeText(short ? step.text ?? step.code ?? "" : step.code ?? "");
      setCopied(true);
    } catch {
      /* no clipboard (an old browser, an iframe without permission): the code stays on screen */
    }
  };
  const label = copied
    ? t("widget.personal.copied", "Copied")
    : short
      ? t("widget.booked.copy_short", "Copy")
      : t("widget.personal.copy", "Copy the code {{code}}", { code: step.code });
  if (short) {
    return (
      <button type="button" onClick={copy} className="underline underline-offset-2">
        {label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="mx-auto mt-2 flex items-center gap-1.5 text-xs text-muted-foreground underline underline-offset-2"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

function OtherChats({ others }: { others: PersonalLink[] }) {
  const { t } = useTranslation();
  const name = useChannelName();
  const usable = others.filter((l) => l.url);
  if (!usable.length) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>{t("widget.booked.or_write_in", "or write in")}</span>
      {usable.map((l) => (
        <a
          key={l.channel}
          href={l.url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-medium text-foreground underline underline-offset-2"
        >
          <Mark id={l.channel} size="sm" /> {name({ id: l.channel })}
        </a>
      ))}
    </div>
  );
}

function QuietLinks({ status, onOpenClientDashboard }: { status: StatusPanel; onOpenClientDashboard?: () => void }) {
  const { t } = useTranslation();
  const links: { key: string; label: string; onClick: () => void }[] = [];
  if (status.onAction) links.push({ key: "again", label: t("widget.booked.book_again", "Book again"), onClick: status.onAction });
  if (onOpenClientDashboard) links.push({ key: "account", label: t("widget.booked.account", "Create an account"), onClick: onOpenClientDashboard });
  if (!links.length) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 pb-8 pt-6 text-[13px] text-muted-foreground">
      {links.map((l) => (
        <button key={l.key} type="button" onClick={l.onClick} className="underline underline-offset-2 hover:text-foreground">
          {l.label}
        </button>
      ))}
    </div>
  );
}
